import React, { useEffect, useRef, useState, createElement } from 'react';
import { EyeIcon, EyeOffIcon } from 'lucide-react';
import { C0, E0, EQ0, M0, ROLES0, S0, TIPOS, U0, moduleCards } from './data';
import { useStoredState, useSessionState } from './storage';
import { useSucursales, useUsuarios, useRoles, useConsumos, useAuditoria, useConfiguracion, guardarUsuario, cambiarPassword } from './db';
import { diferencias, type Campos } from './auditoria';
import { nombreContador } from './contadores';
import type { AppState, CambioAuditoria, Consumo, Empleado, Equipo, Medicion, Perfil, RolPermiso, Sucursal, Usuario, View } from './types';
import { Field, Modal, T, TIPOGRAFIA, glass, inputStyle, primaryButton, ghostButton, useIsMobile, CampoValidado, useConfirmacion } from './ui';
import { LOGO_DATA_URI } from './Logo';
import { Login } from './modules/Login';
import { ConsumoEnergetico } from './modules/ConsumoEnergetico';
import { Alertas } from './modules/Alertas';
import { Auditoria } from './modules/Auditoria';
import { Configuracion } from './modules/Configuracion';
import { Dashboard } from './modules/Dashboard';
import { Reportes } from './modules/Reportes';
import { ReportesOrdenes } from './modules/ReportesOrdenes';
import { RolesPermisos } from './modules/RolesPermisos';
import { Sucursales } from './modules/Sucursales';
import { Usuarios } from './modules/Usuarios';
import { ConexionSupabase } from './modules/ConexionSupabase';
import GasApp from './modules/gas';
import { Pedidos } from './modules/Pedidos';
import { Flota } from './modules/Flota';
import { useFlota, usePedidos } from './modules/gasData';
const uid = () => Date.now().toString(36) + Math.random().toString(36).slice(2, 5);
const stamp = () => new Date().toLocaleString('es-DO', {
  day: '2-digit',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit'
});
const xml = (v: string | number) => String(v).replaceAll('&', '&amp;').replaceAll('<', '&lt;').replaceAll('>', '&gt;').replaceAll('"', '&quot;');
function downloadBlob(filename: string, blob: Blob) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
type ModalKind = 'sucursal' | 'empleado' | 'medicion' | 'equipo' | 'rol' | 'usuario' | null;
type PermFlags = {
  leer: boolean;
  editar: boolean;
  archivar: boolean;
};
// Módulos actuales del sistema (los demás fueron retirados de la app).
// Empleados, mediciones, equipos, historial y mapa salieron de la app hace
// tiempo: seguían apareciendo en el editor de roles, así que se otorgaban
// permisos sobre pantallas que ya no existen. La matriz de "Roles y permisos"
// nunca los mostró, de ahí que las dos listas no coincidieran.
const MODULE_KEYS: string[] = ['inicio', 'consumo', 'costoConsumo', 'sucursales', 'reportes', 'alertas', 'usuarios', 'roles', 'gas', 'pedidos', 'pedidosAutorizar', 'reportesPedidos', 'flota', 'auditoria', 'configuracion'];
const ACCIONES: (keyof PermFlags)[] = ['leer', 'editar', 'archivar'];
const MODULE_LABELS: Record<string, string> = {
  inicio: 'Panel',
  consumo: 'Consumo',
  costoConsumo: 'Ver costo estimado',
  sucursales: 'Sucursales',
  reportes: 'Reportes',
  alertas: 'Alertas',
  usuarios: 'Usuarios',
  roles: 'Roles y permisos',
  gas: 'Gas Industrial',
  pedidos: 'Órdenes de Pedido',
  // Permiso granular (no es una vista): habilita firmar/autorizar una orden,
  // igual que 'costoConsumo' solo destapa un dato dentro de Consumo.
  pedidosAutorizar: 'Autorizar órdenes de pedido',
  reportesPedidos: 'Reportes de Órdenes',
  flota: 'Flota y Destinos',
  auditoria: 'Auditoría',
  configuracion: 'Configuración'
};
// ── QUÉ SE APUNTA EN LA AUDITORÍA DE CADA ENTIDAD ───────────────────────────
// Lista explícita de campos, no las claves del objeto: el registro es para
// leerlo, así que no entran ids internos ni banderas de control. Los campos
// que apuntan a otra tabla (sucursalId, empleadoId) se resuelven a su nombre
// antes de comparar; para eso están las funciones `vista*` de más abajo.
const CAMPOS_SUCURSAL: Campos = {
  nombre: 'Nombre',
  direccion: 'Dirección',
  tipo: 'Tipo'
};
const CAMPOS_EMPLEADO: Campos = {
  nombre: 'Nombre',
  cargo: 'Cargo',
  sucursal: 'Sucursal'
};
const CAMPOS_MEDICION: Campos = {
  sucursal: 'Sucursal',
  punto: 'Punto',
  lux: 'Lux',
  empleado: 'Empleado',
  notas: 'Notas'
};
const CAMPOS_EQUIPO: Campos = {
  nombre: 'Nombre',
  serial: 'Serial',
  estado: 'Estado',
  calibracion: 'Calibración'
};
const CAMPOS_USUARIO: Campos = {
  nombre: 'Nombre',
  correo: 'Correo',
  rol: 'Rol',
  sucursales: 'Sucursales'
};

// Los permisos de un rol no caben en un campo: lo que importa auditar es qué
// cambió módulo por módulo, así que cada módulo tocado sale como una línea
// propia ("Órdenes de Pedido: leer → leer, editar").
const resumirFlags = (p?: PermFlags) => {
  if (!p) return '—';
  const puestos = ACCIONES.filter(a => p[a]);
  return puestos.length ? puestos.join(', ') : 'ninguno';
};
function cambiosDePermisos(antes: Record<string, PermFlags> | undefined, despues: Record<string, PermFlags>): CambioAuditoria[] {
  const salida: CambioAuditoria[] = [];
  for (const modulo of MODULE_KEYS) {
    const viejo = resumirFlags(antes?.[modulo]);
    const nuevo = resumirFlags(despues[modulo]);
    if (!antes) {
      if (nuevo !== 'ninguno' && nuevo !== '—') salida.push({
        campo: MODULE_LABELS[modulo] ?? modulo,
        antes: '',
        despues: nuevo
      });
      continue;
    }
    if (viejo !== nuevo) salida.push({
      campo: MODULE_LABELS[modulo] ?? modulo,
      antes: viejo,
      despues: nuevo
    });
  }
  return salida;
}

// Casilla con tres estados: marcada, vacía, o "a medias" cuando solo una parte
// de lo que agrupa está activo. HTML no tiene atributo para el estado
// intermedio, solo se puede poner por JS sobre el nodo.
function CasillaTri({
  marcadas,
  total,
  onChange,
  titulo
}: {
  marcadas: number;
  total: number;
  onChange: (valor: boolean) => void;
  titulo?: string;
}) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = marcadas > 0 && marcadas < total;
  }, [marcadas, total]);
  return <input ref={ref} type="checkbox" title={titulo} aria-label={titulo} checked={total > 0 && marcadas === total} onChange={e => onChange(e.target.checked)} style={{
    cursor: 'pointer'
  }} data-id="element-0" data-mp="25snsy2z95m6n" />;
}
const emptyPermisos = (): Record<string, PermFlags> => Object.fromEntries(MODULE_KEYS.map(k => [k, {
  // El Panel arranca habilitado: es la pantalla de aterrizaje y un rol sin
  // ninguna vista permitida no tendría dónde caer. Se puede desmarcar.
  leer: k === 'inicio',
  editar: false,
  archivar: false
}]));
export default function App() {
  const isMobile = useIsMobile();
  const [dialogo, confirmar] = useConfirmacion();
  // Confirmación de alta/edición. Un solo helper para todos los módulos, así
  // el texto y el estilo son idénticos en Sucursales, Empleados, Mediciones,
  // Equipos, Roles y Usuarios en vez de repetirse seis veces.
  const confirmarGuardar = (entidad: string, esEdicion: boolean, detalles: {
    etiqueta: string;
    valor: string;
  }[]) => confirmar({
    titulo: esEdicion ? `¿Guardar los cambios?` : `¿Crear ${entidad}?`,
    mensaje: esEdicion ? `Se actualizarán los datos de ${entidad} con lo que está en el formulario.` : `Se agregará ${entidad} al sistema.`,
    detalles: detalles.filter(d => d.valor),
    confirmar: esEdicion ? 'Guardar cambios' : 'Crear',
    tono: 'accent'
  });

  // Confirmación de archivado/desarchivado, también compartida.
  const confirmarArchivar = (entidad: string, nombre: string, archivando: boolean) => confirmar({
    titulo: archivando ? `¿Archivar ${entidad}?` : `¿Desarchivar ${entidad}?`,
    mensaje: archivando ? 'Sale del listado principal y queda en la pestaña "Archivados". No se borra nada y se puede revertir.' : 'Vuelve al listado principal.',
    detalles: [{
      etiqueta: 'Registro',
      valor: nombre
    }],
    confirmar: archivando ? 'Sí, archivar' : 'Sí, desarchivar',
    tono: archivando ? 'warn' : 'accent'
  });

  // Reemplazo de alert(): mismo diálogo del sistema, con un solo botón.
  const avisar = (titulo: string, mensaje?: string) => {
    void confirmar({
      titulo,
      mensaje,
      soloAceptar: true,
      tono: 'warn'
    });
  };
  // Entidades sincronizadas con tablas reales de Supabase (caché local de respaldo)
  const [sucs, setSucs] = useSucursales(S0);
  const [usuarios, setUsuarios] = useUsuarios(U0);
  const [roles, setRoles] = useRoles(ROLES0);
  const [consumos, setConsumos] = useConsumos();
  const {
    pedidos,
    onAddPedido,
    onUpdatePedido,
    onArchivarPedido
  } = usePedidos();
  const flota = useFlota();
  // La sesión NO usa useStoredState: ese hook sincroniza con la tabla remota
  // `app_state`, que es compartida por clave y sin distinción de dispositivo,
  // así que la sesión del último que entrara quedaba disponible para cualquier
  // otro equipo que abriera la app. `useSessionState` la deja solo en este
  // navegador y además la caduca por inactividad y por tiempo absoluto.
  //
  // Va antes de `useAuditoria` porque cada evento se firma con quien lo hizo.
  const [session, setSession] = useSessionState<Usuario>();
  // Quién firma los eventos. Se pasa como función y no como valor porque la
  // sesión cambia (entrar, salir) sin que el hook se vuelva a montar: hay que
  // leerla en el momento de registrar, no en el de montar.
  const sesionRef = useRef<Usuario | null>(session);
  sesionRef.current = session;
  // La vista activa se declara aquí arriba —y no con el resto de estados de
  // pantalla— porque `useAuditoria` la necesita: solo escucha la tabla de
  // auditoría en tiempo real mientras esa pantalla está abierta.
  const [view, setView] = useState<View>('inicio');
  const {
    audit,
    log,
    detalleActivo: auditDetalleActivo
  } = useAuditoria(stamp, () => {
    const s = sesionRef.current;
    return s ? {
      id: s.id,
      nombre: s.nombre
    } : null;
  }, view === 'auditoria');
  // El usuario logueado es exactamente lo que devolvió la Edge Function
  // 'login' (sin password). Ya no se busca en el array local `usuarios`:
  // ese array puede no incluir a este usuario si está filtrado/paginado,
  // y además así evitamos depender de tener la lista completa cargada.
  const sessionUser = session;
  const perfil: Perfil = {
    nombre: sessionUser?.nombre ?? 'Usuario',
    correo: sessionUser?.correo ?? '',
    rol: sessionUser?.rol ?? 'tecnico'
  };
  const {
    notif,
    prefs,
    setNotif,
    setPrefs
  } = useConfiguracion({
    perfil,
    notif: {
      fueraRango: true,
      resumen: true,
      auditoria: false,
      email: true
    },
    prefs: {
      luxMin: 250,
      idioma: 'Español',
      zona: 'America/Santo_Domingo',
      tema: 'Oscuro'
    }
  }, session?.id ?? 'guest');
  // Cambio de contraseña del usuario con la sesión abierta. La contraseña
  // actual se comprueba contra el servidor antes de tocar nada (ver
  // `cambiarPassword` en db.ts); aquí solo se aportan los datos de la sesión,
  // que la Edge Function necesita para no borrar el resto del registro.
  const onCambiarPassword = async (actual: string, nueva: string) => {
    if (!session) return {
      ok: false,
      error: 'No hay sesión abierta.'
    };
    const r = await cambiarPassword({
      id: session.id,
      nombre: session.nombre,
      correo: session.correo,
      rol: session.rol,
      sucursalesIds: session.sucursalesIds,
      archivado: session.archivado,
      passwordActual: actual,
      passwordNueva: nueva
    });
    if (r.ok) log(`Contraseña cambiada: ${session.nombre}`, {
      accion: 'editar',
      entidad: 'Usuario',
      registro: session.nombre,
      // Solo el hecho, nunca la contraseña vieja ni la nueva.
      cambios: [{
        campo: 'Contraseña',
        antes: '(la anterior)',
        despues: '(una nueva)'
      }]
    });
    return r;
  };

  // Al editar el perfil (nombre/correo) se actualiza vía la Edge Function
  // 'guardar-usuario' (no hay escritura directa a la tabla `usuarios`) y se
  // refleja también en la sesión local para que la UI quede sincronizada.
  const setPerfil = async (value: Perfil) => {
    if (!session) return;
    const correoNuevo = value.correo.trim().toLowerCase();
    const enUsoPorOtro = usuarios.some(u => u.id !== session.id && u.correo.trim().toLowerCase() === correoNuevo);
    if (correoNuevo && enUsoPorOtro) {
      avisar('Ese correo ya está en uso', 'Otro usuario del sistema ya tiene registrado ese correo.');
      return;
    }
    const {
      ok,
      error
    } = await guardarUsuario({
      id: session.id,
      nombre: value.nombre,
      correo: value.correo,
      rol: session.rol,
      sucursalesIds: session.sucursalesIds,
      archivado: session.archivado
    });
    if (!ok) {
      avisar('No se pudo actualizar el perfil', error);
      return;
    }
    setSession({
      ...session,
      nombre: value.nombre,
      correo: value.correo
    });
    setUsuarios(prev => prev.map(u => u.id === session.id ? {
      ...u,
      nombre: value.nombre,
      correo: value.correo
    } : u));
    log(`Perfil actualizado: ${value.nombre} (${value.correo})`, {
      accion: 'editar',
      entidad: 'Perfil',
      registro: value.nombre,
      cambios: diferencias({
        nombre: session.nombre,
        correo: session.correo
      }, {
        nombre: value.nombre,
        correo: value.correo
      }, {
        nombre: 'Nombre',
        correo: 'Correo'
      })
    });
  };
  // Aún en localStorage (no forman parte del modelo de tablas pedido).
  //
  // Van en SOLO LECTURA: son de módulos retirados (empleados, mediciones,
  // equipos). Alertas sigue leyendo las mediciones, así que el dato tiene que
  // seguir estando, pero ya no hay pantalla que lo cambie: escribirlo solo
  // podía salir mal, y salió. Ver el comentario de `useStoredState`.
  const [emps, setEmps] = useStoredState<Empleado[]>('getr.empleados', E0, {
    soloLectura: true
  });
  const [meds, setMeds] = useStoredState<Medicion[]>('getr.mediciones', M0, {
    soloLectura: true
  });
  const [equipos, setEquipos] = useStoredState<Equipo[]>('getr.equipos', EQ0, {
    soloLectura: true
  });
  const [dismissedAlerts, setDismissedAlerts] = useStoredState<string[]>('getr.alertas.resueltas', []);
  const [scopeSucId, setScopeSucId] = useState<string>('');
  const [modal, setModal] = useState<ModalKind>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  // Actualizar el título de la pestaña del navegador
  useEffect(() => {
    const moduleName = view === 'inicio' ? 'Panel' : MODULE_LABELS[view] || view;
    document.title = `ELECTRICIDAD | ${moduleName}`;
  }, [view]);
  // form state
  const [sN, setSN] = useState('');
  const [sD, setSD] = useState('');
  const [sT, setST] = useState(TIPOS[0]);
  const [eN, setEN] = useState('');
  const [eC, setEC] = useState('');
  const [eS, setES] = useState('');
  const [mS, setMS] = useState('');
  const [mP, setMP] = useState('');
  const [mL, setML] = useState('');
  const [mE, setME] = useState('');
  const [mN, setMN] = useState('');
  const [eqNombre, setEqNombre] = useState('');
  const [eqSerial, setEqSerial] = useState('');
  const [eqEstado, setEqEstado] = useState('Disponible');
  const [eqCal, setEqCal] = useState('');
  const [rolNombre, setRolNombre] = useState('');
  const [rolDesc, setRolDesc] = useState('');
  const [rolPermisos, setRolPermisos] = useState<Record<string, PermFlags>>(emptyPermisos());
  const [uN, setUN] = useState('');
  const [uCorreo, setUCorreo] = useState('');
  const [uPass, setUPass] = useState('');
  const [showUPass, setShowUPass] = useState(false);
  const [uRol, setURol] = useState<Usuario['rol']>('tecnico');
  const [uSucs, setUSucs] = useState<string[]>([]);
  const state: AppState = {
    sucs,
    emps,
    meds,
    perfil,
    notif,
    prefs,
    audit,
    auditDetalleActivo,
    dismissedAlerts,
    equipos,
    roles,
    usuarios,
    consumos
  };
  // ── SESSION / SCOPING ─────────────────────────────────────────────
  const currentUser = session;
  if (!currentUser) {
    return <Login onLogin={usuario => {
      setSession(usuario);
      // El evento se firma con quien acaba de entrar: `sesionRef` se adelanta
      // porque `setSession` no habrá surtido efecto hasta el próximo render, y
      // entonces el registro saldría sin autor.
      sesionRef.current = usuario;
      log(`Inicio de sesión: ${usuario.nombre}`, {
        accion: 'entrar',
        entidad: 'Sesión',
        registro: usuario.correo,
        cambios: [{
          campo: 'Rol',
          antes: '',
          despues: usuario.rol
        }]
      });
      // Siempre al Panel: es la única vista garantizada para todo rol. Antes
      // los no-admin aterrizaban en Consumo aunque su rol no lo permitiera.
      setView('inicio');
    }} data-id="element-1" data-mp="ahfpqzamxybp" />;
  }
  // Sesiones persistidas por versiones anteriores podían no traer
  // `sucursalesIds`; normalizamos a un arreglo para no romper el render.
  const currentSucursalesIds = Array.isArray(currentUser.sucursalesIds) ? currentUser.sucursalesIds : [];
  // OJO: estos dos conceptos son distintos y antes estaban mezclados en una
  // sola variable `isAdmin`, lo que causaba que CUALQUIER usuario sin
  // sucursales asignadas (el valor por defecto al crear uno nuevo) recibiera
  // acceso total como administrador, sin importar los permisos reales de su
  // rol. Ahora:
  // - `isAdmin`: solo es true para el rol 'admin' real. Controla el bypass
  //   de permisos (canRead/canEdit/canArchive) y el menú completo.
  // - `seesAllSucursales`: true para admin O para cualquier usuario cuyo
  //   `sucursalesIds` esté vacío (convención: [] = "todas"). Solo controla
  //   qué datos/sucursales ve, no qué puede hacer con ellos.
  const isAdmin = currentUser.rol === 'admin';
  const seesAllSucursales = isAdmin || currentSucursalesIds.length === 0;
  const scopeSucursales = seesAllSucursales ? sucs : sucs.filter(s => currentSucursalesIds.includes(s.id));
  // Siempre se ofrece el selector de sucursal en Consumo, aunque solo haya
  // una sucursal disponible (definitivo, a petición del usuario).
  const puntoFijo = false;
  const activeSuc = scopeSucursales.find(s => s.id === scopeSucId) ?? scopeSucursales[0] ?? sucs[0];
  // El detalle de auditoría guarda nombres, no ids: se lee mucho después, y
  // un id no le dice nada a nadie. Si la sucursal ya no existe, el id es mejor
  // que dejar el hueco en blanco.
  const nombreSucursal = (id: string) => sucs.find(s => s.id === id)?.nombre ?? id;
  const logout = () => {
    // Se registra antes de cerrar: después ya no habría de quién firmarlo.
    log(`Cierre de sesión: ${currentUser.nombre}`, {
      accion: 'salir',
      entidad: 'Sesión',
      registro: currentUser.correo
    });
    setSession(null);
    setView('inicio');
  };
  // ── HANDLERS (admin entity CRUD) ──────────────────────────────────
  const openNew = (target: Exclude<ModalKind, null>) => {
    setEditingId(null);
    if (target === 'sucursal') {
      setSN('');
      setSD('');
      setST(TIPOS[0]);
    }
    if (target === 'empleado') {
      setEN('');
      setEC('');
      setES('');
    }
    if (target === 'medicion') {
      setMS('');
      setMP('');
      setML('');
      setME('');
      setMN('');
    }
    if (target === 'equipo') {
      setEqNombre('');
      setEqSerial('');
      setEqEstado('Disponible');
      setEqCal('');
    }
    if (target === 'rol') {
      setRolNombre('');
      setRolDesc('');
      setRolPermisos(emptyPermisos());
    }
    if (target === 'usuario') {
      setUN('');
      setUCorreo('');
      setUPass('');
      setShowUPass(false);
      const primerRol = roles[0]?.id.replace(/^rol-/, '') as Usuario['rol'];
      setURol(primerRol ?? 'tecnico');
      setUSucs([]);
    }
    setModal(target);
  };
  const editSuc = (id: string) => {
    const s = sucs.find(x => x.id === id);
    if (!s) return;
    setEditingId(id);
    setSN(s.nombre);
    setSD(s.direccion);
    setST(s.tipo);
    setModal('sucursal');
  };
  const editRol = (id: string) => {
    const r = roles.find(x => x.id === id);
    if (!r) return;
    setEditingId(id);
    setRolNombre(r.nombre);
    setRolDesc(r.descripcion);
    // Solo se conservan las claves de módulos que siguen existiendo: si el rol
    // trae permisos viejos (mapa, equipos...) se descartan en vez de volver a
    // guardarlos cada vez que se edita.
    const vigentes = emptyPermisos();
    for (const k of MODULE_KEYS) if (r.permisos[k]) vigentes[k] = {
      ...vigentes[k],
      ...r.permisos[k]
    };
    setRolPermisos(vigentes);
    setModal('rol');
  };
  const editUsuario = (id: string) => {
    const u = usuarios.find(x => x.id === id);
    if (!u) return;
    setEditingId(id);
    setUN(u.nombre);
    setUCorreo(u.correo);
    // El campo queda vacío a propósito: la contraseña actual NUNCA llega al
    // cliente. Si se deja vacío al guardar, el servidor conserva el hash actual.
    setUPass('');
    setShowUPass(false);
    setURol(u.rol);
    setUSucs(u.sucursalesIds);
    setModal('usuario');
  };
  const addSuc = async () => {
    if (!sN.trim()) {
      avisar('Ingresa el nombre');
      return;
    }
    if (!(await confirmarGuardar('la sucursal', !!editingId, [{
      etiqueta: 'Nombre',
      valor: sN
    }, {
      etiqueta: 'Dirección',
      valor: sD
    }, {
      etiqueta: 'Tipo',
      valor: sT
    }]))) return;
    const nuevaSuc = {
      nombre: sN,
      direccion: sD,
      tipo: sT
    };
    if (editingId) {
      const previa = sucs.find(s => s.id === editingId);
      setSucs(sucs.map(s => s.id === editingId ? {
        ...s,
        ...nuevaSuc
      } : s));
      log(`Sucursal editada: ${sN}`, {
        accion: 'editar',
        entidad: 'Sucursal',
        registro: sN,
        cambios: diferencias(previa, nuevaSuc, CAMPOS_SUCURSAL)
      });
    } else {
      setSucs([...sucs, {
        id: uid(),
        ...nuevaSuc,
        creado: Date.now()
      }]);
      log(`Sucursal creada: ${sN}`, {
        accion: 'crear',
        entidad: 'Sucursal',
        registro: sN,
        cambios: diferencias(null, nuevaSuc, CAMPOS_SUCURSAL)
      });
    }
    setEditingId(null);
    setModal(null);
  };
  const addEmp = async () => {
    if (!eN.trim()) {
      avisar('Ingresa el nombre');
      return;
    }
    if (!(await confirmarGuardar('el empleado', !!editingId, [{
      etiqueta: 'Nombre',
      valor: eN
    }, {
      etiqueta: 'Cargo',
      valor: eC
    }, {
      etiqueta: 'Sucursal',
      valor: sucs.find(x => x.id === eS)?.nombre ?? ''
    }]))) return;
    // Se compara sobre el nombre de la sucursal, no sobre su id: el detalle se
    // lee meses después y un id no dice nada.
    const vistaEmp = (e?: {
      nombre: string;
      cargo: string;
      sucursalId: string;
    }) => e && {
      nombre: e.nombre,
      cargo: e.cargo,
      sucursal: nombreSucursal(e.sucursalId)
    };
    const nuevoEmp = {
      nombre: eN,
      cargo: eC,
      sucursalId: eS
    };
    if (editingId) {
      const previo = emps.find(e => e.id === editingId);
      setEmps(emps.map(e => e.id === editingId ? {
        ...e,
        ...nuevoEmp
      } : e));
      log(`Empleado editado: ${eN}`, {
        accion: 'editar',
        entidad: 'Empleado',
        registro: eN,
        cambios: diferencias(vistaEmp(previo), vistaEmp(nuevoEmp)!, CAMPOS_EMPLEADO)
      });
    } else {
      setEmps([...emps, {
        id: uid(),
        ...nuevoEmp
      }]);
      log(`Empleado creado: ${eN}`, {
        accion: 'crear',
        entidad: 'Empleado',
        registro: eN,
        cambios: diferencias(null, vistaEmp(nuevoEmp)!, CAMPOS_EMPLEADO)
      });
    }
    setEditingId(null);
    setModal(null);
  };
  const addMed = async () => {
    const lux = parseFloat(mL);
    if (!mS) {
      avisar('Selecciona sucursal');
      return;
    }
    if (!mP.trim()) {
      avisar('Ingresa el punto');
      return;
    }
    if (Number.isNaN(lux) || lux < 0) {
      avisar('Lux invalido');
      return;
    }
    if (!(await confirmarGuardar('la medición', !!editingId, [{
      etiqueta: 'Sucursal',
      valor: sucs.find(x => x.id === mS)?.nombre ?? ''
    }, {
      etiqueta: 'Punto',
      valor: mP
    }, {
      etiqueta: 'Lux',
      valor: String(lux)
    }]))) return;
    const vistaMed = (m?: {
      sucursalId: string;
      punto: string;
      lux: number;
      empleadoId: string;
      notas: string;
    }) => m && {
      sucursal: nombreSucursal(m.sucursalId),
      punto: m.punto,
      lux: m.lux,
      empleado: emps.find(e => e.id === m.empleadoId)?.nombre ?? '',
      notas: m.notas
    };
    const nuevaMed = {
      sucursalId: mS,
      punto: mP,
      lux,
      empleadoId: mE,
      notas: mN
    };
    if (editingId) {
      const previa = meds.find(m => m.id === editingId);
      setMeds(meds.map(m => m.id === editingId ? {
        ...m,
        ...nuevaMed
      } : m));
      log(`Medicion editada: ${mP}`, {
        accion: 'editar',
        entidad: 'Medición',
        registro: mP,
        cambios: diferencias(vistaMed(previa), vistaMed(nuevaMed)!, CAMPOS_MEDICION)
      });
    } else {
      setMeds([{
        id: uid(),
        ...nuevaMed,
        fecha: Date.now()
      }, ...meds]);
      log(`Medicion registrada: ${mP}`, {
        accion: 'crear',
        entidad: 'Medición',
        registro: mP,
        cambios: diferencias(null, vistaMed(nuevaMed)!, CAMPOS_MEDICION)
      });
    }
    setEditingId(null);
    setModal(null);
  };
  const addEquipo = async () => {
    if (!eqNombre.trim()) {
      avisar('Ingresa el nombre del equipo');
      return;
    }
    if (!(await confirmarGuardar('el equipo', !!editingId, [{
      etiqueta: 'Nombre',
      valor: eqNombre
    }, {
      etiqueta: 'Serial',
      valor: eqSerial
    }, {
      etiqueta: 'Estado',
      valor: eqEstado
    }]))) return;
    const nuevoEq = {
      nombre: eqNombre,
      serial: eqSerial,
      estado: eqEstado,
      calibracion: eqCal
    };
    if (editingId) {
      const previo = equipos.find(q => q.id === editingId);
      setEquipos(equipos.map(q => q.id === editingId ? {
        ...q,
        ...nuevoEq
      } : q));
      log(`Equipo editado: ${eqNombre}`, {
        accion: 'editar',
        entidad: 'Equipo',
        registro: eqNombre,
        cambios: diferencias(previo, nuevoEq, CAMPOS_EQUIPO)
      });
    } else {
      setEquipos([...equipos, {
        id: uid(),
        ...nuevoEq
      }]);
      log(`Equipo creado: ${eqNombre}`, {
        accion: 'crear',
        entidad: 'Equipo',
        registro: eqNombre,
        cambios: diferencias(null, nuevoEq, CAMPOS_EQUIPO)
      });
    }
    setEditingId(null);
    setModal(null);
  };
  const addRol = async () => {
    if (!rolNombre.trim()) {
      avisar('Ingresa el nombre del rol');
      return;
    }
    if (!(await confirmarGuardar('el rol', !!editingId, [{
      etiqueta: 'Nombre',
      valor: rolNombre
    }, {
      etiqueta: 'Descripción',
      valor: rolDesc
    }, {
      etiqueta: 'Módulos con lectura',
      valor: String(Object.values(rolPermisos).filter(x => x.leer).length)
    }]))) return;
    const nuevoRol = {
      nombre: rolNombre,
      descripcion: rolDesc,
      permisos: rolPermisos
    };
    const CAMPOS_ROL: Campos = {
      nombre: 'Nombre',
      descripcion: 'Descripción'
    };
    if (editingId) {
      const previo = roles.find(r => r.id === editingId);
      setRoles(roles.map(r => r.id === editingId ? {
        ...r,
        ...nuevoRol
      } : r));
      log(`Rol editado: ${rolNombre}`, {
        accion: 'editar',
        entidad: 'Rol',
        registro: rolNombre,
        cambios: [...diferencias(previo, nuevoRol, CAMPOS_ROL), ...cambiosDePermisos(previo?.permisos, rolPermisos)]
      });
    } else {
      setRoles([...roles, {
        id: uid(),
        ...nuevoRol
      }]);
      log(`Rol creado: ${rolNombre}`, {
        accion: 'crear',
        entidad: 'Rol',
        registro: rolNombre,
        cambios: [...diferencias(null, nuevoRol, CAMPOS_ROL), ...cambiosDePermisos(undefined, rolPermisos)]
      });
    }
    setEditingId(null);
    setModal(null);
  };
  const togglePerm = (modulo: string, accion: keyof PermFlags) => {
    setRolPermisos(prev => {
      const cur = prev[modulo] || {
        leer: false,
        editar: false,
        archivar: false
      };
      return {
        ...prev,
        [modulo]: {
          ...cur,
          [accion]: !cur[accion]
        }
      };
    });
  };
  // Marcar en bloque: la fila entera de un módulo, una columna completa
  // (el mismo permiso en todos los módulos) o la matriz de una vez.
  const marcarFila = (modulo: string, valor: boolean) => setRolPermisos(prev => ({
    ...prev,
    [modulo]: {
      leer: valor,
      editar: valor,
      archivar: valor
    }
  }));
  const marcarColumna = (accion: keyof PermFlags, valor: boolean) => setRolPermisos(prev => Object.fromEntries(MODULE_KEYS.map(m => [m, {
    ...(prev[m] ?? {
      leer: false,
      editar: false,
      archivar: false
    }),
    [accion]: valor
  }])) as Record<string, PermFlags>);
  const marcarTodo = (valor: boolean) => setRolPermisos(Object.fromEntries(MODULE_KEYS.map(m => [m, {
    leer: valor,
    editar: valor,
    archivar: valor
  }])));
  const contarColumna = (accion: keyof PermFlags) => MODULE_KEYS.filter(m => rolPermisos[m]?.[accion]).length;
  // En el teléfono las tres columnas se estrechan para que el nombre del módulo
  // no quede cortado en "Ver costo est...".
  const gridPermisos = isMobile ? 'minmax(0,1fr) repeat(3,34px)' : 'minmax(0,1fr) repeat(3,68px)';
  const permisosDados = MODULE_KEYS.reduce((n, m) => n + ACCIONES.filter(a => rolPermisos[m]?.[a]).length, 0);
  const permisosPosibles = MODULE_KEYS.length * ACCIONES.length;
  const toggleUSuc = (id: string) => setUSucs(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const addUsuario = async () => {
    if (!uN.trim()) {
      avisar('Ingresa el nombre');
      return;
    }
    if (!uCorreo.trim()) {
      avisar('Ingresa el correo');
      return;
    }
    if (!editingId && !uPass.trim()) {
      {
        avisar('Ingresa una contraseña para el nuevo usuario.');
        return;
      }
    }
    if (uPass.trim() && uPass.trim().length < 6) {
      {
        avisar('La contraseña debe tener al menos 6 caracteres.');
        return;
      }
    }
    const sucsIds = uRol === 'admin' ? [] : uSucs;
    if (!(await confirmarGuardar('el usuario', !!editingId, [{
      etiqueta: 'Nombre',
      valor: uN
    }, {
      etiqueta: 'Correo',
      valor: uCorreo
    }, {
      etiqueta: 'Rol',
      valor: roles.find(r => r.id === uRol || r.id === `rol-${uRol}`)?.nombre ?? uRol
    }, {
      etiqueta: 'Sucursales',
      valor: sucsIds.length ? String(sucsIds.length) : 'Todas'
    }]))) return;
    const id = editingId ?? uid();
    const {
      ok,
      error
    } = await guardarUsuario({
      id,
      nombre: uN,
      correo: uCorreo,
      password: uPass.trim() || undefined,
      rol: uRol,
      sucursalesIds: sucsIds
    });
    if (!ok) {
      avisar('No se pudo guardar el usuario', error);
      return;
    }
    const nuevoUsuario = {
      nombre: uN,
      correo: uCorreo,
      rol: uRol,
      sucursalesIds: sucsIds
    };
    const previoUsuario = editingId ? usuarios.find(u => u.id === editingId) : undefined;
    // Sucursales vacías significa "todas": así es como lo entiende el resto de
    // la app, y así tiene que leerse en el registro.
    const vistaUsuario = (u?: {
      nombre: string;
      correo: string;
      rol: string;
      sucursalesIds: string[];
    }) => u && {
      nombre: u.nombre,
      correo: u.correo,
      rol: roles.find(r => r.id === u.rol || r.id === `rol-${u.rol}`)?.nombre ?? u.rol,
      sucursales: u.sucursalesIds?.length ? u.sucursalesIds.map(nombreSucursal).join(', ') : 'Todas'
    };
    if (editingId) {
      setUsuarios(usuarios.map(u => u.id === editingId ? {
        ...u,
        ...nuevoUsuario
      } : u));
      // Si te estás editando a ti mismo, la sesión también tiene que
      // actualizarse: la cabecera, el "AUTORIZADO POR" de los pedidos y el
      // alcance por sucursal salen de ahí, no de la lista de usuarios. Antes
      // seguías viendo tu nombre viejo hasta cerrar sesión.
      if (session && session.id === editingId) {
        setSession({
          ...session,
          nombre: uN,
          correo: uCorreo,
          rol: uRol,
          sucursalesIds: sucsIds
        });
      }
      log(`Usuario editado: ${uN}`, {
        accion: 'editar',
        entidad: 'Usuario',
        registro: uN,
        // La contraseña no entra nunca en el detalle, ni siquiera como
        // "cambiada→cambiada": el registro de auditoría lo lee más gente que
        // la que puede tocar usuarios. Que se cambió queda dicho en su propio
        // evento ("Contraseña cambiada: …").
        cambios: diferencias(vistaUsuario(previoUsuario), vistaUsuario(nuevoUsuario)!, CAMPOS_USUARIO)
      });
    } else {
      setUsuarios([...usuarios, {
        id,
        ...nuevoUsuario
      }]);
      log(`Usuario creado: ${uN}`, {
        accion: 'crear',
        entidad: 'Usuario',
        registro: uN,
        cambios: diferencias(null, vistaUsuario(nuevoUsuario)!, CAMPOS_USUARIO)
      });
    }
    setEditingId(null);
    setModal(null);
  };
  const deleteSuc = async (id: string) => {
    const suc = sucs.find(s => s.id === id);
    if (!suc) return;
    const archivado = !suc.archivado;
    if (!(await confirmarArchivar('esta sucursal', suc.nombre, archivado))) return;
    setSucs(sucs.map(s => s.id === id ? {
      ...s,
      archivado
    } : s));
    log(`Sucursal ${archivado ? 'archivada' : 'desarchivada'}: ${suc.nombre}`, {
      accion: archivado ? 'archivar' : 'desarchivar',
      entidad: 'Sucursal',
      registro: suc.nombre,
      cambios: [{
        campo: 'Archivada',
        antes: archivado ? 'No' : 'Sí',
        despues: archivado ? 'Sí' : 'No'
      }]
    });
  };
  const deleteRol = async (id: string) => {
    const rol = roles.find(x => x.id === id);
    if (!rol) return;
    const archivado = !rol.archivado;
    if (!(await confirmarArchivar('este rol', rol.nombre, archivado))) return;
    setRoles(roles.map(x => x.id === id ? {
      ...x,
      archivado
    } : x));
    log(`Rol ${archivado ? 'archivado' : 'desarchivado'}: ${rol.nombre}`, {
      accion: archivado ? 'archivar' : 'desarchivar',
      entidad: 'Rol',
      registro: rol.nombre,
      cambios: [{
        campo: 'Archivado',
        antes: archivado ? 'No' : 'Sí',
        despues: archivado ? 'Sí' : 'No'
      }]
    });
  };
  // Archivar un usuario TIENE que pasar por la Edge Function: la tabla
  // `usuarios` tiene RLS estricto y el cliente anónimo no puede escribirla.
  // Antes esto solo tocaba el estado local, así que el cambio se perdía en
  // la siguiente carga, cuando 'listar-usuarios' vuelve a traer la lista del
  // servidor y la sobrescribe. Por eso "no funcionaba el archivado".
  const deleteUsuario = async (id: string) => {
    const u = usuarios.find(x => x.id === id);
    if (!u) return;
    const archivado = !u.archivado;
    if (!(await confirmarArchivar('este usuario', u.nombre, archivado))) return;
    const {
      ok,
      error
    } = await guardarUsuario({
      id: u.id,
      nombre: u.nombre,
      correo: u.correo,
      rol: u.rol,
      sucursalesIds: u.sucursalesIds,
      archivado
    });
    if (!ok) {
      avisar('No se pudo archivar el usuario', error);
      return;
    }
    setUsuarios(usuarios.map(x => x.id === id ? {
      ...x,
      archivado
    } : x));
    log(`Usuario ${archivado ? 'archivado' : 'desarchivado'}: ${u.nombre}`, {
      accion: archivado ? 'archivar' : 'desarchivar',
      entidad: 'Usuario',
      registro: u.nombre,
      cambios: [{
        campo: 'Archivado',
        antes: archivado ? 'No' : 'Sí',
        despues: archivado ? 'Sí' : 'No'
      }]
    });
  };
  const deleteConsumo = async (id: string) => {
    const c = consumos.find(x => x.id === id);
    if (!c) return;
    const archivado = !c.archivado;
    const nombreSuc = sucs.find(su => su.id === c.sucursalId)?.nombre ?? c.sucursalId;
    const deQuien = (c.contador ?? '').trim();
    if (!(await confirmarArchivar('esta lectura', `${nombreSuc}${deQuien ? ` — ${deQuien}` : ''} — ${c.kwh} kWh`, archivado))) return;
    setConsumos(consumos.map(x => x.id === id ? {
      ...x,
      archivado
    } : x));
    const nombre = sucs.find(su => su.id === c.sucursalId)?.nombre ?? c.sucursalId;
    log(`Consumo ${archivado ? 'archivado' : 'desarchivado'} en ${nombre}: ${c.kwh} kWh`, {
      accion: archivado ? 'archivar' : 'desarchivar',
      entidad: 'Consumo',
      registro: `${nombre} — ${c.kwh} kWh`,
      cambios: [{
        campo: 'Archivado',
        antes: archivado ? 'No' : 'Sí',
        despues: archivado ? 'Sí' : 'No'
      }]
    });
  };
  const addConsumo = (sucursalId: string, contador: string, fecha: number, kwh: number) => {
    setConsumos([...consumos, {
      id: uid(),
      sucursalId,
      contador: contador.trim(),
      fecha,
      kwh
    }]);
    const nombre = sucs.find(s => s.id === sucursalId)?.nombre ?? sucursalId;
    log(`Consumo registrado en ${nombre} (${contador}): ${kwh} kWh`, {
      accion: 'crear',
      entidad: 'Consumo',
      registro: `${nombre} — ${contador} — ${kwh} kWh`,
      cambios: diferencias(null, {
        sucursal: nombre,
        contador,
        kwh,
        fecha: new Date(fecha).toLocaleDateString('es-DO')
      }, {
        sucursal: 'Sucursal',
        contador: 'Contador',
        kwh: 'kWh',
        fecha: 'Fecha'
      })
    });
  };

  // ── PERMISOS DEL ROL ──────────────────────────────────────────────
  // El rol del usuario se mapea a su definición en `roles` para saber qué
  // módulos puede leer. Los roles "semilla" (admin/supervisor/tecnico)
  // guardan su id como 'rol-<rol>', pero los roles personalizados creados
  // desde el módulo "Roles y permisos" guardan su id tal cual (sin prefijo),
  // y eso es justamente lo que se guarda en `usuario.rol`. Por eso probamos
  // primero el id exacto y, si no existe, probamos con el prefijo 'rol-'.
  const userRole = roles.find(r => r.id === currentUser.rol) ?? roles.find(r => r.id === `rol-${currentUser.rol}`);
  const permisos = userRole?.permisos ?? {};
  // Solo algunos roles ven el costo estimado en Consumo (permiso 'costoConsumo').
  const canSeeCosto = isAdmin || !!permisos.costoConsumo?.leer;
  // Firmar una orden de pedido es una atribución aparte de poder crearla: un
  // despachador arma el borrador, pero solo quien tiene 'pedidosAutorizar'
  // estampa su nombre en el "AUTORIZADO POR" del formulario.
  const canAutorizarPedidos = isAdmin || !!permisos.pedidosAutorizar?.leer;
  // Todos los módulos, incluido el Panel, pasan por el permiso del rol.
  // Excepción de compatibilidad: los roles creados antes de que 'inicio'
  // fuera otorgable no lo tienen guardado, y quedarse sin Panel de golpe los
  // dejaría sin pantalla de aterrizaje. Por eso ausente = permitido, y solo
  // un `false` explícito lo niega.
  const canRead = (id: View) => {
    if (isAdmin) return true;
    if (id === 'inicio') return permisos.inicio?.leer !== false;
    return !!permisos[id]?.leer;
  };
  const canEdit = (id: View) => isAdmin || !!permisos[id]?.editar;
  const canArchive = (id: View) => isAdmin || !!permisos[id]?.archivar;
  // ── NAV ───────────────────────────────────────────────────────────
  // Módulos visibles del producto (los demás fueron retirados de la app).
  const ALLOWED_NAV_KEYS: string[] = ['sucursales', 'reportes', 'alertas', 'gas', 'pedidos', 'reportesPedidos', 'flota', 'auditoria', 'configuracion'];
  // Orden en que se muestran las vistas y en que se busca un respaldo cuando
  // la activa no está permitida.
  const ORDEN_VISTAS: View[] = ['inicio', 'consumo', ...(ALLOWED_NAV_KEYS as View[])];
  // Una sola construcción para admin y no-admin: el bypass del admin ya está
  // dentro de canRead.
  const nav: {
    id: View;
    label: string;
  }[] = ORDEN_VISTAS.filter(canRead).map(v => ({
    id: v,
    label: MODULE_LABELS[v] ?? v
  }));
  // Primera vista que el rol sí puede abrir. Puede no haber ninguna si le
  // quitaron todos los permisos, incluido el Panel.
  const primeraVista = ORDEN_VISTAS.find(canRead);

  // ── ALCANCE DE DATOS ──────────────────────────────────────────────
  // Para usuarios no-admin, todas las vistas se limitan a sus sucursales
  // asignadas. El admin (sucursalesIds vacío) ve todo.

  const viewSucs = seesAllSucursales ? sucs : scopeSucursales;
  const scopeIds = new Set(scopeSucursales.map(s => s.id));
  const viewMeds = seesAllSucursales ? meds : meds.filter(m => scopeIds.has(m.sucursalId));
  const viewEmps = seesAllSucursales ? emps : emps.filter(e => scopeIds.has(e.sucursalId));
  const viewConsumos = seesAllSucursales ? consumos : consumos.filter(c => scopeIds.has(c.sucursalId));
  const viewState: AppState = seesAllSucursales ? state : {
    ...state,
    sucs: viewSucs,
    meds: viewMeds,
    emps: viewEmps,
    consumos: viewConsumos
  };

  // El usuario solo ve y elige entre las sucursales que tiene asignadas
  // (admin = todas). `scopeSucursales` y `activeSuc` ya respetan ese alcance.
  const consumoScreen = activeSuc ? <ConsumoEnergetico state={viewState} sucursal={activeSuc} sucursalesDisponibles={scopeSucursales} puntoFijo={puntoFijo} mostrarCosto={canSeeCosto} onChangeSucursal={setScopeSucId} onSave={addConsumo} onDelete={canArchive('consumo') ? deleteConsumo : undefined} canArchive={canArchive('consumo')} data-id="element-2" data-mp="1x4n3f9jy3wkg" /> : <div style={{
    color: T.textMuted,
    padding: 40,
    textAlign: 'center'
  }} data-id="element-3" data-mp="2wt10a6tsand">

      No tienes ningún punto comercial asignado.
    </div>;
  const noop = () => {};
  const pages: Record<string, React.ReactNode> = {
    consumo: consumoScreen,
    inicio: <Dashboard state={viewState} setView={setView} data-id="element-4" data-mp="3pzxu8u92rc64" />,
    sucursales: <Sucursales sucs={viewSucs} onNew={canEdit('sucursales') ? () => openNew('sucursal') : undefined} onEdit={canEdit('sucursales') ? editSuc : undefined} onDelete={canArchive('sucursales') ? deleteSuc : undefined} canArchive={canArchive('sucursales')} data-id="element-5" data-mp="qm3wpd9bjeun" />,
    gas: <GasApp data-id="element-6" data-mp="cndytveodb58" />,
    pedidos: <Pedidos pedidos={pedidos} onAdd={onAddPedido} onUpdate={onUpdatePedido} onArchivar={onArchivarPedido} conductores={flota.conductores} envasadoras={flota.envasadoras} camiones={flota.camiones} colas={flota.colas} puedeVerFlota={canRead('flota')} canEdit={canEdit('pedidos')} canArchive={canArchive('pedidos')} canAutorizar={canAutorizarPedidos} usuarioNombre={perfil.nombre} usuarioCargo={userRole?.nombre ?? currentUser.rol} onLog={log} data-id="element-7" data-mp="cm2u9tdenzd2" />,
    flota: <Flota camiones={flota.camiones} colas={flota.colas} envasadoras={flota.envasadoras} conductores={flota.conductores} onAddCamion={flota.onAddCamion} onUpdateCamion={flota.onUpdateCamion} onArchivarCamion={flota.onArchivarCamion} onAddCola={flota.onAddCola} onUpdateCola={flota.onUpdateCola} onArchivarCola={flota.onArchivarCola} onAddEnvasadora={flota.onAddEnvasadora} onUpdateEnvasadora={flota.onUpdateEnvasadora} onArchivarEnvasadora={flota.onArchivarEnvasadora} onAddConductor={flota.onAddConductor} onUpdateConductor={flota.onUpdateConductor} onArchivarConductor={flota.onArchivarConductor} pendientes={flota.pendientes} canEdit={canEdit('flota')} canArchive={canArchive('flota')} onLog={log} data-id="element-8" data-mp="1lvr195vphsii" />,
    reportes: <Reportes state={viewState} onLog={log} data-id="element-9" data-mp="f70y3o2ng9pp" />,
    reportesPedidos: <ReportesOrdenes pedidos={pedidos} conductores={flota.conductores} camiones={flota.camiones} colas={flota.colas} envasadoras={flota.envasadoras} usuarioNombre={perfil.nombre} onLog={log} data-id="element-10" data-mp="f9dbdnxgpvwf" />,
    alertas: <Alertas state={viewState} resolveAlert={id => {
      setDismissedAlerts([...dismissedAlerts, id]);
      log('Alerta resuelta', {
        accion: 'resolver',
        entidad: 'Alerta',
        registro: id
      });
    }} data-id="element-11" data-mp="3c2zkx9ojwkh0" />,
    auditoria: <Auditoria state={state} data-id="element-12" data-mp="2xb8stm7e7oni" />,
    configuracion: <Configuracion state={state} setPerfil={setPerfil} setNotif={setNotif} setPrefs={setPrefs} usuariosNode={canRead('usuarios') ? <Usuarios state={state} onNew={canEdit('usuarios') ? () => openNew('usuario') : noop} onEdit={canEdit('usuarios') ? editUsuario : noop} onDelete={canArchive('usuarios') ? deleteUsuario : noop} canArchive={canArchive('usuarios')} data-id="element-14" data-mp="371tnwznuyb1o" /> : undefined} rolesNode={canRead('roles') ? <RolesPermisos roles={roles} onNew={canEdit('roles') ? () => openNew('rol') : noop} onEdit={canEdit('roles') ? editRol : noop} onDelete={canArchive('roles') ? deleteRol : noop} canArchive={canArchive('roles')} data-id="element-15" data-mp="2ejfzfqoew1f9" /> : undefined} conexionNode={isAdmin ? <ConexionSupabase data-id="element-16" data-mp="tvdiugzkopyk" /> : undefined} cambiarPassword={onCambiarPassword} data-id="element-13" data-mp="2x9xonla1cevv" />
  };
  // Si la vista activa no está permitida se cae a la primera que sí lo esté.
  // Si el rol no tiene ninguna, se dice claramente en vez de mostrar una
  // pantalla en blanco o colar un módulo negado.
  const sinAcceso = <div style={{
    ...glass,
    padding: 40,
    textAlign: 'center',
    color: T.textSecondary,
    maxWidth: 520,
    margin: '40px auto'
  }} data-id="element-17" data-mp="17umz1gns3k2j">
      <div style={{
      fontSize: 17,
      fontWeight: 800,
      color: T.text,
      marginBottom: 8
    }} data-id="element-18" data-mp="1crvhzjgr3s4u">
        Sin módulos habilitados
      </div>
      Tu rol no tiene permiso de lectura sobre ningún módulo. Pídele a un
      administrador que te habilite al menos uno en Roles y permisos.
    </div>;
  const page = canRead(view) ? pages[view] : primeraVista ? pages[primeraVista] : sinAcceso;
  return <div style={{
    minHeight: '100vh',
    fontFamily: TIPOGRAFIA,
    background: '#FFFCF8',
    color: '#1A1207'
  }} data-id="element-19" data-mp="3oh6080z1iceq">
      
      <header style={{
      ...glass,
      position: 'sticky',
      top: 0,
      zIndex: 100,
      borderRadius: 0,
      borderLeft: 'none',
      borderRight: 'none',
      borderTop: 'none',
      padding: isMobile ? '10px 14px' : '12px 24px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      flexWrap: isMobile ? 'wrap' : 'nowrap',
      gap: isMobile ? 10 : 18
    }} data-id="element-20" data-mp="1p3pbtnm9wrkr">
        
        <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        flexShrink: 0
      }} data-id="element-21" data-mp="16c4uxivi67n6">
          
          <img src={LOGO_DATA_URI} alt="Junquito Gas" style={{
          height: isMobile ? 26 : 76,
          width: 'auto',
          maxWidth: isMobile ? '28vw' : 'none',
          objectFit: 'contain',
          display: 'block'
        }} data-id="element-22" data-mp="xd1aobklnm73" />
          
        </div>
        <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: isMobile ? 8 : 12,
        flexShrink: 0,
        marginLeft: isMobile ? 'auto' : 0,
        order: isMobile ? 1 : 3
      }} data-id="element-23" data-mp="2rru0tv2prkrn">
          
          <div style={{
          textAlign: 'right',
          lineHeight: 1.25
        }} data-id="element-24" data-mp="n92wwvggrdj4">
            
            <div style={{
            fontSize: 12.5,
            fontWeight: 700,
            color: '#1A1207'
          }} data-id="element-25" data-mp="rag4pb349qso">
              
              {currentUser.nombre}
            </div>
            <div style={{
            fontSize: 11,
            color: T.textMuted,
            textTransform: 'capitalize'
          }} data-id="element-26" data-mp="3vowcn82z0kz4">
              
              {userRole?.nombre ?? currentUser.rol}
            </div>
          </div>
          <button onClick={logout} style={{
          ...ghostButton,
          padding: '7px 12px'
        }} data-id="element-27" data-mp="8l9mrqa5d37x">
            
            Salir
          </button>
        </div>
        <nav style={{
        display: 'flex',
        gap: isMobile ? 6 : 4,
        flexWrap: isMobile ? 'nowrap' : 'wrap',
        justifyContent: isMobile ? 'flex-start' : 'center',
        order: isMobile ? 3 : 2,
        width: isMobile ? '100%' : 'auto',
        overflowX: isMobile ? 'auto' : 'visible',
        WebkitOverflowScrolling: 'touch',
        marginTop: isMobile ? 4 : 0,
        paddingTop: isMobile ? 8 : 0,
        paddingBottom: isMobile ? 2 : 0,
        borderTop: isMobile ? `1px solid ${T.border}` : 'none'
      }} data-id="element-28" data-mp="1jyu3vu5xyt5t">
          
          {nav.map(item => {
          const active = view === item.id;
          return <button key={item.id} onClick={() => setView(item.id)} style={{
            padding: '7px 12px',
            borderRadius: 10,
            border: 'none',
            background: active ? 'rgba(255,107,1,.12)' : 'transparent',
            color: active ? T.accent : T.textMuted,
            fontSize: 12.5,
            fontWeight: active ? 700 : 600,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            flexShrink: 0
          }} data-id="element-29" data-mp="19devpplwwe3y">
                
                {item.label}
              </button>;
        })}
        </nav>
      </header>

      <main style={{
      padding: isMobile ? '20px 14px 48px' : '32px 24px 60px',
      maxWidth: 1160,
      margin: '0 auto'
    }} data-id="element-30" data-mp="3f1nmarotd3yk">
        
        {page}
      </main>

      {modal === 'sucursal' && <Modal title={editingId ? 'Editar sucursal' : 'Nueva sucursal'} onClose={() => {
      setModal(null);
      setEditingId(null);
    }} data-id="element-31" data-mp="368ak0z4v0pzt">
        
          <Field label="NOMBRE" data-id="element-32" data-mp="2re61xh7vp85m">
            <CampoValidado modo="letras" valor={sN} onChange={setSN} placeholder="Ej: Plaza Central" data-id="element-33" data-mp="2wbfq6tjj7drv" />
          
          </Field>
          <Field label="DIRECCION" data-id="element-34" data-mp="2icppz51f0xza">
            <input style={inputStyle} value={sD} onChange={e => setSD(e.target.value)} placeholder="Calle, ciudad" data-id="element-35" data-mp="2idhjmabgclmx" />
          
          </Field>
          <Field label="TIPO" data-id="element-36" data-mp="gkthk4ajo48q">
            <select style={inputStyle} value={sT} onChange={e => setST(e.target.value)} data-id="element-37" data-mp="gkjlq02tj2v4">
            
              {TIPOS.map(t => <option key={t} data-id="element-38" data-mp="lgiioc27pt75">{t}</option>)}
            </select>
          </Field>
          <button onClick={addSuc} style={{
        ...primaryButton,
        width: '100%'
      }} data-id="element-39" data-mp="1ncc1ylyb5ssc">
          
            {editingId ? 'Guardar cambios' : 'Guardar'}
          </button>
        </Modal>}
      {modal === 'empleado' && <Modal title={editingId ? 'Editar empleado' : 'Nuevo empleado'} onClose={() => {
      setModal(null);
      setEditingId(null);
    }} data-id="element-40" data-mp="21zf2l1hxghjw">
        
          <Field label="NOMBRE" data-id="element-41" data-mp="260h3dmrht1nz">
            <CampoValidado modo="letras" valor={eN} onChange={setEN} data-id="element-42" data-mp="263d57m6goahp" />
          
          </Field>
          <Field label="CARGO" data-id="element-43" data-mp="2b1wmh9a40y3q">
            <CampoValidado modo="letras" valor={eC} onChange={setEC} data-id="element-44" data-mp="2fywv9rycgeyj" />
          
          </Field>
          <Field label="SUCURSAL" data-id="element-45" data-mp="2kyj8i1e69v7c">
            <select style={inputStyle} value={eS} onChange={e => setES(e.target.value)} data-id="element-46" data-mp="2kxrf8x81s9k8">
            
              <option value="" data-id="element-47" data-mp="ln00bp0p0qyq">Sin asignar</option>
              {sucs.map(s => <option key={s.id} value={s.id} data-id="element-48" data-mp="ql9vw0qbxtkv">
                  {s.nombre}
                </option>)}
            </select>
          </Field>
          <button onClick={addEmp} style={{
        ...primaryButton,
        width: '100%'
      }} data-id="element-49" data-mp="33wmg89sqnvf2">
          
            {editingId ? 'Guardar cambios' : 'Guardar'}
          </button>
        </Modal>}
      {modal === 'medicion' && <Modal title={editingId ? 'Editar medicion' : 'Nueva medicion'} onClose={() => {
      setModal(null);
      setEditingId(null);
    }} data-id="element-50" data-mp="2q33ggkfu30qb">
        
          <Field label="SUCURSAL" data-id="element-51" data-mp="3msue2sjf940c">
            <select style={inputStyle} value={mS} onChange={e => setMS(e.target.value)} data-id="element-52" data-mp="3irsn0yznry9g">
            
              <option value="" data-id="element-53" data-mp="3dspgfa415jzd">Seleccionar</option>
              {viewSucs.map(s => <option key={s.id} value={s.id} data-id="element-54" data-mp="3dquxg1cbk1xx">
                  {s.nombre}
                </option>)}
            </select>
          </Field>
          <Field label="PUNTO" data-id="element-55" data-mp="22d94e53c9g74">
            <input style={inputStyle} value={mP} onChange={e => setMP(e.target.value)} data-id="element-56" data-mp="22eby0ks1pwqq" />
          
          </Field>
          <Field label="LUX" data-id="element-57" data-mp="1xd4p2ie62rih">
            <CampoValidado modo="numeros" valor={mL} onChange={setML} placeholder="0" maxLength={6} data-id="element-58" data-mp="1yvlhmox65a5g" />
          
          </Field>
          <Field label="EMPLEADO" data-id="element-59" data-mp="23uy3p7g7ud6z">
            <select style={inputStyle} value={mE} onChange={e => setME(e.target.value)} data-id="element-60" data-mp="23tws91vafooq">
            
              <option value="" data-id="element-61" data-mp="1pundm6mqd2mv">Seleccionar</option>
              {emps.map(e => <option key={e.id} value={e.id} data-id="element-62" data-mp="1usylibtgt32g">
                  {e.nombre}
                </option>)}
            </select>
          </Field>
          <Field label="NOTAS" data-id="element-63" data-mp="1g30rhf8utj5c">
            <textarea style={{
          ...inputStyle,
          resize: 'none'
        }} rows={2} value={mN} onChange={e => setMN(e.target.value)} data-id="element-64" data-mp="1kvk7sjyxesdu" />
          
          </Field>
          <button onClick={addMed} style={{
        ...primaryButton,
        width: '100%'
      }} data-id="element-65" data-mp="172m5edo2g00s">
          
            {editingId ? 'Guardar cambios' : 'Registrar'}
          </button>
        </Modal>}
      {modal === 'equipo' && <Modal title={editingId ? 'Editar equipo' : 'Nuevo equipo'} onClose={() => {
      setModal(null);
      setEditingId(null);
    }} data-id="element-66" data-mp="125vo5n0iyyds">
        
          <Field label="NOMBRE" data-id="element-67" data-mp="ijsslftlowfl">
            <input style={inputStyle} value={eqNombre} onChange={e => setEqNombre(e.target.value)} data-id="element-68" data-mp="dkpllpu28g4z" />
          
          </Field>
          <Field label="SERIAL" data-id="element-69" data-mp="sirr45knjbov">
            <CampoValidado modo="codigo" valor={eqSerial} onChange={setEqSerial} data-id="element-70" data-mp="sj1km34wo0zb" />
          
          </Field>
          <Field label="ESTADO" data-id="element-71" data-mp="nkq6vhbhp6bt">
            <select style={inputStyle} value={eqEstado} onChange={e => setEqEstado(e.target.value)} data-id="element-72" data-mp="11hwo37gkewyx">
            
              <option data-id="element-73" data-mp="wiacf2gdxos8">Disponible</option>
              <option data-id="element-74" data-mp="wh91cxzdd2ai">En uso</option>
              <option data-id="element-75" data-mp="1b8rcoihaa8nj">Mantenimiento</option>
            </select>
          </Field>
          <Field label="PROXIMA CALIBRACION" data-id="element-76" data-mp="1694yo6tmsodo">
            <input style={inputStyle} value={eqCal} onChange={e => setEqCal(e.target.value)} data-id="element-77" data-mp="16ag2u7qzny5b" />
          
          </Field>
          <button onClick={addEquipo} style={{
        ...primaryButton,
        width: '100%'
      }} data-id="element-78" data-mp="1k95ydiy5tprk">
          
            {editingId ? 'Guardar cambios' : 'Guardar'}
          </button>
        </Modal>}
      {modal === 'rol' && <Modal title={editingId ? 'Editar rol' : 'Nuevo rol'} onClose={() => {
      setModal(null);
      setEditingId(null);
    }} data-id="element-79" data-mp="3ep4qb5e3inuf">
        
          <Field label="NOMBRE" data-id="element-80" data-mp="3jgw7ipbd3h7w">
            <CampoValidado modo="letras" valor={rolNombre} onChange={setRolNombre} data-id="element-81" data-mp="3jhxvfudh7y71" />
          
          </Field>
          <Field label="DESCRIPCION" data-id="element-82" data-mp="3ogqwgnbktwqb">
            <input style={inputStyle} value={rolDesc} onChange={e => setRolDesc(e.target.value)} data-id="element-83" data-mp="2qrz23ms91ewk" />
          
          </Field>
          <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 10,
        marginBottom: 8
      }} data-id="element-84" data-mp="2vqsetczpp1vo">

            <span style={{
          fontSize: 11,
          fontWeight: 700,
          color: '#1A1207',
          letterSpacing: '0.08em'
        }} data-id="element-85" data-mp="34r71afoewn0r">

              PERMISOS
              <span style={{
            marginLeft: 8,
            fontWeight: 600,
            letterSpacing: 0,
            color: T.textMuted
          }} data-id="element-86" data-mp="k0g37awjh8f9">

                {permisosDados} de {permisosPosibles}
              </span>
            </span>
            <button type="button" onClick={() => marcarTodo(permisosDados < permisosPosibles)} style={{
          ...ghostButton,
          padding: '6px 12px',
          fontSize: 12
        }} data-id="element-87" data-mp="20hym2zr543wt">

              {permisosDados < permisosPosibles ? 'Marcar todo' : 'Quitar todo'}
            </button>
          </div>
          {/* Cabecera de la matriz: cada casilla marca ese permiso en todos los
           módulos de golpe. Queda "a medias" si solo algunos lo tienen. */}
          <div style={{
        display: 'grid',
        gridTemplateColumns: gridPermisos,
        alignItems: 'end',
        gap: isMobile ? 4 : 8,
        padding: isMobile ? '0 8px 6px' : '0 10px 6px'
      }} data-id="element-88" data-mp="1csm3j99xajhu">

            <span style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: '0.06em',
          color: T.textMuted
        }} data-id="element-89" data-mp="3753jdvsfmhcg">

              MÓDULO
            </span>
            {ACCIONES.map(a => <label key={a} style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 3,
          cursor: 'pointer',
          fontSize: isMobile ? 9 : 10,
          fontWeight: 700,
          letterSpacing: '0.04em',
          color: T.textMuted,
          textTransform: 'uppercase'
        }} data-id="element-90" data-mp="3l9ugixz5mbaf">

                {isMobile ? {
            leer: 'Ver',
            editar: 'Edit',
            archivar: 'Arch'
          }[a] : a}
                <CasillaTri marcadas={contarColumna(a)} total={MODULE_KEYS.length} onChange={v => marcarColumna(a, v)} titulo={`Marcar "${a}" en todos los módulos`} data-id="element-91" data-mp="cuj421xq8ibu" />
              </label>)}
          </div>
          <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
        maxHeight: 260,
        overflowY: 'auto',
        marginBottom: 16,
        paddingRight: 4
      }} data-id="element-92" data-mp="3st2i3a4s30c">

            {MODULE_KEYS.map(m => {
          const flags = rolPermisos[m] || {
            leer: false,
            editar: false,
            archivar: false
          };
          const enFila = ACCIONES.filter(a => flags[a]).length;
          return <div key={m} style={{
            display: 'grid',
            gridTemplateColumns: gridPermisos,
            alignItems: 'center',
            gap: isMobile ? 4 : 8,
            padding: isMobile ? '8px' : '8px 10px',
            background: T.surface,
            border: `1px solid ${T.border}`,
            borderRadius: 8
          }} data-id="element-93" data-mp="2q2jzrz57h27j">

                  <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: isMobile ? 5 : 7,
              minWidth: 0,
              cursor: 'pointer'
            }} data-id="element-94" data-mp="1t9onu0ufumt4">

                    <CasillaTri marcadas={enFila} total={ACCIONES.length} onChange={v => marcarFila(m, v)} titulo={`Marcar todo en ${MODULE_LABELS[m] ?? m}`} data-id="element-95" data-mp="1emvdzb3ou98b" />
                    <span style={{
                fontSize: isMobile ? 11 : 12,
                fontWeight: 700,
                color: '#1A1207',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }} data-id="element-96" data-mp="19n9152rruv01">

                      {MODULE_LABELS[m] ?? m}
                    </span>
                  </label>
                  {/* La tercera acción es 'archivar', que es lo que lee
                   canArchive(). Antes decía 'eliminar', una clave que
                   nadie consulta: el permiso de archivar quedaba
                   imposible de otorgar a un rol no-admin. */}
                  {ACCIONES.map(a => <label key={a} title={`${a} · ${MODULE_LABELS[m] ?? m}`} style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer'
            }} data-id="element-97" data-mp="1gsv235tl841d">

                        <input type="checkbox" aria-label={`${a} · ${MODULE_LABELS[m] ?? m}`} checked={flags[a]} onChange={() => togglePerm(m, a)} style={{
                cursor: 'pointer'
              }} data-id="element-98" data-mp="1qp7snutu61rw" />
                      </label>)}
                </div>;
        })}
          </div>
          <button onClick={addRol} style={{
        ...primaryButton,
        width: '100%'
      }} data-id="element-99" data-mp="3b7s3dfg0y71e">
          
            {editingId ? 'Guardar cambios' : 'Guardar'}
          </button>
        </Modal>}
      {modal === 'usuario' && <Modal title={editingId ? 'Editar usuario' : 'Nuevo usuario'} onClose={() => {
      setModal(null);
      setEditingId(null);
    }} data-id="element-100" data-mp="31djlrmsj7yi0">
        
          <Field label="NOMBRE" data-id="element-101" data-mp="3u0vyz6csytyn">
            <CampoValidado modo="letras" valor={uN} onChange={setUN} placeholder="Nombre y apellido" data-id="element-102" data-mp="3u1xlc6z9r2vo" />
          
          </Field>
          <Field label="CORREO" data-id="element-103" data-mp="3kab0rk11jrg9">
            <input style={inputStyle} type="text" value={uCorreo} onChange={e => setUCorreo(e.target.value)} placeholder="correo@getr.do" data-id="element-104" data-mp="3k8zw7hzrufo3" />
          
          </Field>
          <Field label={editingId ? 'CONTRASEÑA (dejar vacío para mantener)' : 'CONTRASEÑA'} data-id="element-105" data-mp="gtkowois2k6n">
          
            <div style={{
          position: 'relative'
        }} data-id="element-106" data-mp="27eof4wotbbi4">
            
              <input style={{
            ...inputStyle,
            paddingRight: 42
          }} type={showUPass ? 'text' : 'password'} autoComplete="new-password" value={uPass} onChange={e => setUPass(e.target.value)} placeholder="••••••" data-id="element-107" data-mp="22f248twgi7ch" />
            

              <button type="button" onClick={() => setShowUPass(s => !s)} aria-label={showUPass ? 'Ocultar contraseña' : 'Mostrar contraseña'} style={{
            position: 'absolute',
            right: 6,
            top: '50%',
            transform: 'translateY(-50%)',
            background: 'transparent',
            border: 'none',
            color: T.textMuted,
            padding: 6,
            display: 'flex',
            cursor: 'pointer'
          }} data-id="element-108" data-mp="1tfyjzmljzlwe">
              
                {showUPass ? <EyeOffIcon size={17} data-id="element-109" data-mp="39u6ugit1u4ro" /> : <EyeIcon size={17} data-id="element-110" data-mp="39uglm9ptyhyy" />}
              </button>
            </div>
          </Field>
          <Field label="ROL" data-id="element-111" data-mp="3502j7ncr3aks">
            <select style={inputStyle} value={uRol} onChange={e => setURol(e.target.value as Usuario['rol'])} data-id="element-112" data-mp="3cdla80qeazrz">
            
              {roles.length === 0 && <option value="" disabled data-id="element-113" data-mp="3hceqg0x2h4vu">
                  No hay roles en la base de datos
                </option>}
              {roles.map(r => <option key={r.id} value={r.id.replace(/^rol-/, '')} data-id="element-114" data-mp="3qcur72mfdxx0">
                  {r.nombre}
                </option>)}
            </select>
          </Field>
          {uRol !== 'admin' && <Field label="PUNTOS COMERCIALES ASIGNADOS" data-id="element-115" data-mp="3va3ax7n82a0d">
              <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 6
        }} data-id="element-116" data-mp="44l4xf7t6abc">
            
                {sucs.map(s => <label key={s.id} style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            cursor: 'pointer',
            fontSize: 13,
            color: T.textSecondary,
            padding: '6px 8px',
            background: T.surface,
            border: `1px solid ${T.border}`,
            borderRadius: 8
          }} data-id="element-117" data-mp="8ypdg2wq2qej">
              
                    <input type="checkbox" checked={uSucs.includes(s.id)} onChange={() => toggleUSuc(s.id)} data-id="element-118" data-mp="2iwwf2hvppi76" />
              

                    {s.nombre}
                  </label>)}
              </div>
            </Field>}
          {uRol === 'admin' && <div style={{
        fontSize: 12,
        color: '#c4b5fd',
        marginBottom: 16
      }} data-id="element-119" data-mp="2ny3mugxuuhdu">
          
              El administrador tiene acceso a todos los puntos comerciales.
            </div>}
          <button onClick={addUsuario} style={{
        ...primaryButton,
        width: '100%'
      }} data-id="element-120" data-mp="2xn3pqbfh9wxb">
          
            {editingId ? 'Guardar cambios' : 'Crear usuario'}
          </button>
        </Modal>}
      {dialogo}
    </div>;
}